/** @jsxImportSource @emotion/react */

import React, { useEffect, useState } from 'react';
import NavActions from 'Services/Navigation/Actions';
import { Col, Container, Row } from 'reactstrap';
import FeaturedProduct from 'Containers/Home/Components/FeaturedProduct';
import Selectors from 'Redux/Selectors';
import { IProductDetails } from 'Redux/slices/products/Entities';
import { RootState } from 'Redux/store';
import Actions from 'Redux/Actions';
import { ICartProduct, IProduct, IProductWidget } from '@ddot/AddToCartCommon/lib/entities';
import { connect } from 'react-redux';
import PageSkeleton from 'Components/Skeletons/PageSkeleton';
import { RouteComponentProps } from 'react-router-dom';
import { PaymentDetailsApiResponse } from 'Redux/slices/cart/controllers/getPaymentDetails';
import Utils from 'Lib/Utils';
import { homeBtn, container, text, title, contRelated, titleRelated, textDetails, recieptText } from './Styles/PaymentSuccessStyles';

interface PaymentDetailsResponse {
    payload: PaymentDetailsApiResponse;
}
interface HomeProps {
    productWidgetsLoading: boolean;
    productWidgets: IProductWidget[] | undefined;
    featuredProducts: IProduct[] | undefined;
    featuredProductsLoading: boolean;
    match: {
        params: { paymentID: string }
    };
    getPaymentDetails: (paymentId: string) => PaymentDetailsResponse;
    clearCart(): void;
    userId: string;
}

enum IPaymentTransactionStatus {
    Pending = 0,
    Success = 2,
    Failed = 3,
    Processing = 4,
}

const PaymentSuccess = (props: HomeProps): JSX.Element => {
    const { productWidgets, featuredProducts, featuredProductsLoading, match: { params: { paymentID } }, getPaymentDetails, clearCart, userId } = props;

    const [paymentDetails, setPaymentDetails] = useState<PaymentDetailsApiResponse | undefined>();
    const [loadingPaymentDetails, setLoadingPaymentDetails] = useState(false);

    const paymentStatus = Utils.Jemisys.getPaymentTransactionStatus(paymentDetails?.paymentStatus as IPaymentTransactionStatus);

    useEffect(() => {
        handleGetDetails();
    }, []);

    useEffect(() => {
        handleClearCart();
    }, [userId]);

    const handleGetDetails = async () => {
        setLoadingPaymentDetails(true);

        const getDetails = await getPaymentDetails(paymentID);

        if (getDetails) {
            setPaymentDetails(getDetails.payload);
            setLoadingPaymentDetails(false);
        }
    };

    const handleClearCart = () => {
        const cartId = `cart-${userId}`;

        localStorage.removeItem(cartId);
        clearCart();
    };

    const navToHome = () => {
        NavActions.navToHome();
    };

    const renderProducts = () => {
        const { productWidgetsLoading } = props;

        if (productWidgetsLoading) return <PageSkeleton css={{ width: '100%' }} />;
        if (!productWidgets || !productWidgets.length) return null;

        const bestSellers = productWidgets.find(item => item.title === 'New Arrivals');

        if (bestSellers) {
            return bestSellers.products.slice(0, 4).map(items => (
                <Col xs={6} sm={4} md={3} key={items.id} className='p-0'>
                    <FeaturedProduct product={items} type='list' />
                </Col>
            ));
        }

        return null;
    };

    return (
        <div>
            <div css={container}>
                <div className='cont1'>
                    <img src='/assets/icons/paymentSuccess.svg' alt='order success' />
                    <h1 css={title}>
                        Order Processing!
                    </h1>

                    <div className='col-12 mb-5 p-3 border'>

                        {
                            loadingPaymentDetails ? (<PageSkeleton />)
                                : (
                                    <>
                                        <p css={text}>
                                            Payment Status:
                                            {' '}
                                            {paymentStatus}
                                        </p>

                                        <p css={text}>
                                            Order Number:
                                            {' '}
                                            <span style={{ textDecoration: 'underline' }}>
                                                {paymentDetails?.orderNumber}
                                            </span>
                                        </p>
                                        {
                                            paymentDetails?.products.map((item: ICartProduct) => (
                                                <p key={item.id} css={recieptText}>
                                                    {item.name}
                                                    {' '}
                                                    x
                                                    {item.quantity}
                                                </p>
                                            ))
                                        }

                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <p css={recieptText}>
                                                Total Price:
                                                {' '}
                                            </p>
                                            <p css={recieptText}>
                                                {paymentDetails?.currency}
                                                {paymentDetails?.amountPaid}
                                            </p>
                                        </div>
                                    </>
                                )
                        }
                    </div>

                    <p css={text}>Thank you for choosing Tiansi. A confirmation email with your order details has been sent to you.</p>
                    <div
                        role='button'
                        tabIndex={0}
                        onClick={navToHome}
                        onKeyPress={navToHome}
                        css={homeBtn}
                    >
                        <p>Back to Home</p>
                        <img src='/assets/icons/ArrowRight.png' alt='' />
                    </div>
                </div>
            </div>
            <div>
                <Container fluid css={contRelated}>
                    <div css={titleRelated}>You may also like</div>
                    <Row xs={1} sm={4} md={5} style={{ marginTop: 20 }}>
                        {renderProducts()}
                    </Row>
                </Container>
            </div>
        </div>
    );
};

const mapStateToProps = (state: RootState) => ({
    productWidgets: Selectors.getHomeProductWidgets(state),
    productWidgetsLoading: Selectors.getHomeProductWidgetsAttempting(state),
    userId: Selectors.getUserId(state),
});

const mapDispatchToProps = (dispatch: any) => ({
    getPaymentDetails: (paymentId: string) => dispatch(Actions.cartGetPaymentDetails({ paymentId })),
    clearCart: () => dispatch(Actions.cartClearCart()),
});

export default connect(mapStateToProps, mapDispatchToProps)(PaymentSuccess);
